import { AlertTriangle } from "@untitledui/icons";
import { useState, type ReactNode } from "react";
import { Dialog, DialogTrigger, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button, type ButtonProps } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";

/** A button whose action must be agreed to first. `body` is anything the action needs asked in the dialog. The dialog stays open, busy, until the action settles. */
export function ConfirmButton({ title, description, body, confirmLabel, destructive, onConfirm, children, ...button }: { title: string; description?: ReactNode; body?: ReactNode; confirmLabel: string; destructive?: boolean; onConfirm: () => Promise<void> } & Omit<ButtonProps, "onClick">) {
  const [busy, setBusy] = useState(false);
  return (
    <DialogTrigger>
      <Button {...button}>{children}</Button>
      <ModalOverlay isDismissable={!busy}>
        <Modal>
          <Dialog role="alertdialog" aria-label={title}>
            {({ close }) => (
              <div className="w-full max-w-100 rounded-xl bg-primary p-6 shadow-xl">
                <FeaturedIcon icon={AlertTriangle} color={destructive ? "error" : "warning"} theme="light" size="lg" />
                <h2 className="mt-4 text-lg font-semibold text-primary">{title}</h2>
                {description && <p className="mt-1 text-sm text-tertiary">{description}</p>}
                {body && <div className="mt-5">{body}</div>}
                <div className="mt-8 flex gap-3">
                  <Button color="secondary" size="lg" className="flex-1" isDisabled={busy} onClick={close}>
                    取消
                  </Button>
                  <Button
                    color={destructive ? "primary-destructive" : "primary"}
                    size="lg"
                    className="flex-1"
                    isLoading={busy}
                    showTextWhileLoading
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await onConfirm();
                        close();
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </div>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}
